import { Controller } from "@hotwired/stimulus";
import { PreviewCanvas } from "@frabr/fabric-editor";
import { posterLayers, thumbnailLayers } from "../lib/composition_document";
import { hasLiveBindings, interpolateLayers, withoutHiddenLayers } from "../lib/variable_interpolation";
import { ensureFontsForLayers } from "../lib/font_loader";

/**
 * Preview fabric readonly d'une page de composition (plan §6.8, bench PROJ-751) : un
 * PreviewCanvas vivant par vignette — la version dépouillée du package (StaticCanvas : pas
 * d'upper canvas, d'écouteurs, de sélection ni de managers). Toute preview se rend depuis le
 * document, jamais depuis un OutputFile.
 *
 * Deux entrées exclusives :
 *  - layers + width + height : une page images, rendue telle quelle ;
 *  - doc : une composition vidéo entière, réduite à son poster (posterLayers).
 *
 * Sans layer à montrer, le placeholder du markup reste visible (média en génération, compo
 * vide). Naming: "composition-preview" in HTML.
 */

// Le rendu courant est-il un PREVIEW ? Vers une page qu'il a en cache, Turbo peint deux fois :
// d'abord le snapshot gardé en mémoire — affichage instantané —, puis la réponse du serveur
// quand elle arrive. Chaque rendu remonte les contrôleurs et repeint les vignettes, si bien
// que révéler celles du premier les ferait disparaître au second, le temps que fabric
// repeigne : le fond réapparaît entre deux vignettes pleines.
//
// Turbo marque `data-turbo-preview` sur <html> pendant un rendu de preview et le retire au
// rendu final. C'est le seul critère juste : le TYPE de visite n'en dit rien — un `advance`
// vers une page en cache peint deux fois, un `advance` vers une page neuve une seule.
const isPreviewRender = () => document.documentElement.hasAttribute("data-turbo-preview");

// Les vignettes peintes pendant un preview, retenues sous leur fond — révélées en bloc dès
// que le rendu final est posé.
const pending = new Set();

// La révélation est différée d'une tâche : turbo:load tombe AU MILIEU de la vague de reveal du
// rendu final (les rendus fabric s'achèvent de façon asynchrone, les uns après les autres).
// Vider la file sur-le-champ laisserait les reveal suivants s'appliquer un par un — des
// vignettes qui apparaissent en ordre dispersé, ce qu'on cherche justement à éviter. Un tour
// de boucle plus tard, la vague est passée et tout se révèle d'un coup.
function flushPending() {
  setTimeout(() => {
    pending.forEach((controller) => controller.reveal());
    pending.clear();
  }, 0);
}

document.addEventListener("turbo:load", flushPending);
// Filet : une visite qui n'aboutit pas (erreur réseau, formulaire refusé) n'émet pas de
// turbo:load, et les vignettes resteraient sous leur fond.
document.addEventListener("turbo:visit-failed", flushPending);
document.addEventListener("turbo:fetch-request-error", flushPending);

export default class extends Controller {
  static targets = ["canvas", "frame", "placeholder", "hint"];

  static values = {
    // La vue se cadre sur le contenu (l'union des calques), pas sur la page : la vignette
    // d'un sticker, dont la page est un espace de travail (PreviewCanvas#fitToContent).
    contentFit: Boolean,
    layers: Array,
    width: Number,
    height: Number,
    doc: Object,
    fit: { type: String, default: "contain" },
    // Les valeurs du lecteur pour les champs liés (le document ne porte que des tokens).
    variables: Object,
    // LEGACY_ERB : un fond HTML (famille ERB) est servi sous le canvas — le damier d'attente
    // du fond nu s'efface pour le laisser voir.
    htmlBackground: Boolean,
  };

  connect() {
    this._connected = true;
    // L'état de départ ne peut pas venir du markup : le snapshot du cache restaure un frame
    // DÉJÀ révélé (il photographie la page après coup), si bien que le rendu du cache montre
    // sa vignette avant que le rendu suivant ne l'efface. On masque donc ici, à chaque montage
    // pendant une visite — seul moyen d'avoir le même point de départ quelle que soit la
    // provenance du HTML.
    //
    // Uniquement s'il y a un fond dessous : masquer une vignette sans fond créerait un trou
    // gris là où l'utilisateur voyait l'image, l'inverse du but recherché.
    if (isPreviewRender() && this.hasFrameTarget && this.hasHintTarget) {
      this.frameTarget.classList.add("opacity-0");
    }
    this.render();
  }

  // Disposal différé : un déplacement DOM (réordonnancement idiomorph ou keyed) passe par
  // disconnect/connect — le canvas et son bitmap survivent au déplacement, on ne dispose
  // que si l'élément est toujours détaché à la frame suivante (vraie suppression).
  disconnect() {
    this._connected = false;
    clearTimeout(this._refitTimer);
    pending.delete(this);
    requestAnimationFrame(() => {
      if (this.element.isConnected) return;

      this._resizeObserver?.disconnect();
      this._resizeObserver = null;
      this._canvas?.dispose();
      this._canvas = null;
    });
  }

  // Guard sur _connected, pas sur _canvas : Stimulus tire les valueChanged avant connect()
  // (seul cas à taire — connect rendra), mais une vignette née vide n'a PAS de canvas
  // (show() ne le crée qu'avec des layers) et doit se rendre quand ses premiers arrivent.
  layersValueChanged() {
    if (this._connected) this.render();
  }

  // Le poster d'un îlot vidéo : sa value est réécrite par le morph (broadcast-signal), sans
  // reconnexion du contrôleur — même angle mort que layers.
  docValueChanged() {
    if (this._connected) this.render();
  }

  // Les rendus s'enchaînent (jamais deux showLayers entrelacés sur le même canvas). Le catch
  // garde la chaîne vivante : sans lui, un seul échec (image morte…) rendrait la vignette
  // définitivement inerte, tous les rendus suivants s'accrochant à une promesse rejetée.
  render() {
    this._chain = (this._chain || Promise.resolve())
      .then(() => this.show())
      .catch((error) => console.error("[composition-preview] rendu échoué", error));
  }

  async show() {
    // La chaîne est asynchrone : entre le connect() et ce tour de boucle, le frame a pu être
    // remplacé (broadcast-signal, morph) et notre élément détaché. Monter fabric sur un
    // canvas hors du document vivant échoue (Firefox : NS_ERROR_ABORT au getContext) — et
    // le rendu serait invisible de toute façon. La nouvelle instance prendra le relais.
    if (!this.element.isConnected) return;

    const { width, height, layers, background } = this.page();
    this.togglePlaceholder(layers.length === 0 && !background);
    if (!layers.length && !background) return;

    const canvas = this.ensureCanvas(width, height);
    // Le fond d'un segment poster sans média (aplat, dégradé) : une valeur CSS peinte SOUS
    // le buffer transparent du canvas — fabric ne connaît pas les dégradés CSS.
    if (this._canvasEl) this._canvasEl.style.background = background || "";
    if (!layers.length) return this.reveal();

    // Les polices que ces layers utilisent, chargées avant le rendu (sinon fallback système
    // silencieux) — à la demande et dédupliquées, voir font_loader.
    await ensureFontsForLayers(layers);
    // Une preview imite le rendu publié : un token sans valeur disparaît (contrairement à
    // l'éditeur, qui montre les trous), un calque masqué par sa condition n'y est pas. Et
    // comme les valeurs interpolées n'ont pas la longueur des tokens, la mise en page se
    // recalcule au rendu (relayout).
    const bound = hasLiveBindings(layers);
    const values = this.variablesValue || {};
    const resolved = bound ? interpolateLayers(withoutHiddenLayers(layers, values), values) : layers;
    // Le palier des images chargées : refit le compare pour savoir s'il faut les recharger.
    this._renderedBound = this.sourceBound(canvas);
    await canvas.showLayers(thumbnailLayers(resolved, this._renderedBound), { relayout: bound });
    if (this.contentFitValue) {
      canvas.fitToContent();
      canvas.requestRenderAll();
    }
    if (this.htmlBackgroundValue) this.concealPendingBackground(canvas);
    this.reveal();
  }

  // LEGACY_ERB : le fond nu d'une page dont le HTML est servi dessous — la lib efface son
  // damier (le cadre reste en attente ; une vignette n'écrit jamais rien de toute façon).
  concealPendingBackground(canvas) {
    const bg = canvas.getObjects().find((object) => object.layerId === "bg");
    if (!bg?.pending) return;

    bg.concealPlaceholder();
    canvas.requestRenderAll();
  }

  // Le canvas peint se révèle PAR-DESSUS le fond, qui reste en place dessous. Jamais l'inverse
  // (retirer le fond) : le fond est le seul état que le serveur sait rendre, et c'est lui qui
  // assure la continuité quand la page revient du cache Turbo puis se re-rend — à aucun
  // instant la vignette n'est vide. Opaque une fois peint, le canvas le masque de toute façon.
  reveal() {
    if (!this.hasFrameTarget) return;
    // Rendu de preview : Turbo vient de peindre le snapshot du cache et le remplacera par la
    // réponse du serveur. Révéler maintenant ferait disparaître la vignette au rendu suivant,
    // le temps que fabric repeigne. On la garde sous le fond, et tout se révèle d'un coup à la
    // fin de la visite. Le Set dédoublonne, un contrôleur démonté s'en retire au disconnect.
    // Sans fond dessous, rien à retenir : la montrer vaut mieux qu'un trou gris.
    if (isPreviewRender() && this.hasHintTarget) return void pending.add(this);

    this.frameTarget.classList.remove("opacity-0");
  }

  // La borne des sources image : le buffer réel (connu après fitToSize) × DPR, snappé au
  // palier supérieur — quatre dérivations Cloudinary partagées par toutes les surfaces, et un
  // dessin proche du 1:1 au canvas (le resize de qualité appartient au CDN).
  sourceBound(canvas) {
    const side = Math.max(canvas.width, canvas.height) * (window.devicePixelRatio || 1);
    return [128, 256, 512, 1024].find((bound) => bound >= side) || 1024;
  }

  page() {
    if (this.hasDocValue && this.docValue.segments) return posterLayers(this.docValue);

    return {
      width: this.widthValue || 1080,
      height: this.heightValue || 1080,
      layers: this.layersValue || [],
    };
  }

  // Le buffer est à la taille d'affichage (fitToSize), les layers restent en espace
  // composition. La boîte se mesure, et une boîte se remesure : montée dans un panneau
  // encore caché (toolbox), elle vaut 0 au premier rendu — le buffer prend alors une taille
  // de repli et l'observateur le réajuste quand elle apparaît, comme à tout changement de
  // largeur (colonne, fenêtre).
  ensureCanvas(width, height) {
    if (this._canvas) return this._canvas;

    this._canvas = new PreviewCanvas(this.freshCanvasElement(), { width, height });
    this.fitCanvas(this.layoutBox());
    this._resizeObserver = new ResizeObserver(() => this.refit());
    this._resizeObserver.observe(this.element);
    return this._canvas;
  }

  // La boîte de mise en page, sans les transforms : une vignette zoomée au survol (carte de
  // Créations) ne doit pas grossir son buffer.
  layoutBox() {
    return { width: this.element.offsetWidth, height: this.element.offsetHeight };
  }

  // Cadrée sur le contenu : le buffer épouse la boîte (sa forme est celle du contenu, posée
  // par le serveur), fitToContent y cadre ensuite la vue.
  fitCanvas(box) {
    this._fitted = [box.width, box.height];
    this._canvas.fitToSize(box.width || 96, box.height || 96, this.contentFitValue ? "cover" : this.fitValue);
  }

  // Une boîte qui change de taille (colonne, fenêtre qu'on redimensionne) : le canvas se recadre
  // et se redessine tout de suite avec ce qu'il a — instantané, sans clignotement. Un rendu
  // complet ne suit que si le palier des sources change (un canvas de repli a chargé des
  // images trop petites pour la vraie boîte), et une fois le redimensionnement posé : relancé
  // à chaque pas, il viderait et rechargerait la vignette des dizaines de fois par seconde.
  refit() {
    if (!this._canvas || !this._connected) return;

    const box = this.layoutBox();
    if (!box.width || !box.height) return;
    if (box.width === this._fitted[0] && box.height === this._fitted[1]) return;

    this.fitCanvas(box);
    if (this.contentFitValue) this._canvas.fitToContent();
    // Tout de suite, pas à la frame suivante : redimensionner a déjà effacé le buffer.
    this._canvas.renderAll();

    // Chaque changement de taille repousse le rendu complet : il n'a lieu qu'une fois, 200 ms
    // après le dernier.
    clearTimeout(this._refitTimer);
    if (this.sourceBound(this._canvas) === this._renderedBound) return;
    this._refitTimer = setTimeout(() => this._connected && this.render(), 200);
  }

  // Fabric reçoit toujours un canvas créé par le document vivant. Le canvas du markup ne
  // convient jamais tel quel : issu du parsing Turbo (stream/frame adopté par le morph), son
  // getContext("2d") échoue sur Firefox (NS_ERROR_ABORT — ex: slide ajoutée au strip) ; et
  // marqué data-fabric, c'est un clone (ghost Sortable, morph) que fabric refuse.
  freshCanvasElement() {
    const fresh = document.createElement("canvas");
    fresh.dataset.compositionPreviewTarget = "canvas";
    this.canvasTarget.replaceWith(fresh);
    // La référence vivante : fabric enveloppe l'élément, le fond CSS se pose dessus.
    this._canvasEl = fresh;
    return fresh;
  }

  togglePlaceholder(show) {
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.toggle("hidden", !show);
    if (this.hasFrameTarget) this.frameTarget.classList.toggle("hidden", show);
  }
}
